import type { DealFlow, DealFlowMetric } from "../../types/dealFunnel";
import {
  COLORS,
  FONTS,
  SLIDE_W,
  TITLE_H,
  addGradientPanel,
  addShape,
  addTitleBar,
  getShapes,
  type PptxInstance,
  type PptxSlide,
} from "./pptxCore";

const SVG_W = 720;
const SVG_H = 620;

type FunnelStageDef = {
  key: keyof DealFlow["funnel"];
  label: string;
  color: string;
  showLess?: boolean;
  stackVolumes?: boolean;
  yRange: [number, number];
};

const FUNNEL_STAGES: FunnelStageDef[] = [
  {
    key: "deals_logged",
    label: "Deals Logged",
    color: "E67E22",
    yRange: [0, 0.22],
  },
  {
    key: "deals_killed",
    label: "Deals Killed",
    color: "C0392B",
    showLess: true,
    yRange: [0.22, 0.44],
  },
  {
    key: "lost_to_other_lenders",
    label: "Lost to Other Lenders",
    color: "8E9A9D",
    showLess: true,
    yRange: [0.44, 0.66],
  },
  {
    key: "active_deals",
    label: "Active Deals",
    color: "1E7A4A",
    stackVolumes: true,
    yRange: [0.66, 1],
  },
];

type BreakdownDef = {
  key: keyof DealFlow["active_breakdown"];
  label: string;
  bg: string;
};

const BREAKDOWN_ITEMS: BreakdownDef[] = [
  { key: "closed", label: "Closed", bg: "1B5E3B" },
  { key: "closed_apollo", label: "Closed - Apollo", bg: "2C7A5A" },
  { key: "signed_term_sheets", label: "Signed Term Sheets", bg: "1A3352" },
  { key: "pending_responses", label: "Pending Responses", bg: "7FB3C9" },
  { key: "in_screening", label: "In Screening", bg: "9A8B7A" },
  { key: "apollo_referrals", label: "Apollo Referrals", bg: "A8AEB5" },
];

function emptyMetric(): DealFlowMetric {
  return { count: 0, volume_gbp: 0, volume_eur: 0, volume_usd: 0 };
}

function formatBn(amount: number, symbol: string): string {
  if (!Number.isFinite(amount)) return `${symbol}—`;
  const bn = amount / 1_000_000_000;
  return `${symbol}${bn.toLocaleString("en-GB", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}BN`;
}

function formatVolumesInline(m: DealFlowMetric): string {
  return [
    formatBn(m.volume_gbp, "£"),
    formatBn(m.volume_eur, "€"),
    formatBn(m.volume_usd, "$"),
  ].join("/");
}

function halfWidthAt(t: number): number {
  return 0.5 * (1 - t);
}

function bandCorners(y0: number, y1: number) {
  return {
    topL: (0.5 - halfWidthAt(y0)) * SVG_W,
    topR: (0.5 + halfWidthAt(y0)) * SVG_W,
    botL: (0.5 - halfWidthAt(y1)) * SVG_W,
    botR: (0.5 + halfWidthAt(y1)) * SVG_W,
    topY: y0 * SVG_H,
    botY: y1 * SVG_H,
  };
}

function svgToIn(
  svgX: number,
  svgY: number,
  funnelW: number,
  funnelH: number,
): { x: number; y: number } {
  return {
    x: (svgX / SVG_W) * funnelW,
    y: (svgY / SVG_H) * funnelH,
  };
}

function svgFontToPt(svgFontSize: number, funnelW: number): number {
  return (svgFontSize / SVG_W) * funnelW * 72;
}

export function addDealFlowOverviewSlide(
  pptx: PptxInstance,
  dealFlow: DealFlow,
  logoDataUri?: string | null,
): PptxSlide {
  const slide = pptx.addSlide();
  slide.background = { color: COLORS.white };

  const { RECTANGLE, ROUNDED_RECTANGLE, CUSTOM_GEOMETRY } = getShapes(pptx);
  const logged = dealFlow.funnel.deals_logged ?? emptyMetric();
  const active = dealFlow.funnel.active_deals ?? emptyMetric();

  addTitleBar(pptx, slide, {
    title: "Pipeline Analysis – Deal Flow Summary",
    logoDataUri,
  });

  const summaryY = TITLE_H;
  const summaryH = 0.5;
  addGradientPanel(pptx, slide, {
    x: 0,
    y: summaryY,
    w: SLIDE_W,
    h: summaryH,
    from: COLORS.navyMid,
    to: COLORS.navyMidLight,
    direction: "horizontal",
    bands: 16,
  });
  slide.addText(
    `${logged.count.toLocaleString("en-GB")} opportunities have been reviewed to date, with ${active.count.toLocaleString("en-GB")} active deals currently progressing through closing, referral and screening stages.`,
    {
      x: 0.35,
      y: summaryY,
      w: SLIDE_W - 0.7,
      h: summaryH,
      fontFace: FONTS.body,
      fontSize: 11,
      color: COLORS.white,
      align: "center",
      valign: "middle",
      margin: 0,
    },
  );

  const bodyTop = summaryY + summaryH;
  const bodyPadTop = 0.28;
  const bodyPadBottom = 0.32;
  const bodyPadX = 0.28;
  const gap = 0.38;

  const funnelH = 7.5 - bodyTop - bodyPadTop - bodyPadBottom;
  const funnelW = (SVG_W / SVG_H) * funnelH;
  const funnelX = bodyPadX;
  const funnelY = bodyTop + bodyPadTop;

  const countPt = svgFontToPt(48, funnelW);
  const labelPt = svgFontToPt(17, funnelW);
  const volsPt = svgFontToPt(14, funnelW);
  const lessPt = svgFontToPt(13, funnelW);

  for (const stage of FUNNEL_STAGES) {
    const [y0, y1] = stage.yRange;
    const c = bandCorners(y0, y1);
    const tl = svgToIn(c.topL, c.topY, funnelW, funnelH);
    const tr = svgToIn(c.topR, c.topY, funnelW, funnelH);
    const br = svgToIn(c.botR, c.botY, funnelW, funnelH);
    const bl = svgToIn(c.botL, c.botY, funnelW, funnelH);

    addShape(slide, CUSTOM_GEOMETRY, {
      x: funnelX,
      y: funnelY,
      w: funnelW,
      h: funnelH,
      fill: { color: stage.color },
      line: { color: stage.color, width: 0 },
      points: [
        { x: tl.x, y: tl.y, moveTo: true },
        { x: tr.x, y: tr.y },
        { x: br.x, y: br.y },
        { x: bl.x, y: bl.y },
        { close: true },
      ],
    });

    const metric = dealFlow.funnel[stage.key] ?? emptyMetric();
    const bandHSvg = (y1 - y0) * SVG_H;
    const contentTopSvg = stage.showLess ? y0 * SVG_H + 26 : y0 * SVG_H + 8;
    const contentBottomSvg = y1 * SVG_H - 8;
    const midYSvg = (contentTopSvg + contentBottomSvg) / 2;

    const midHalfW = halfWidthAt((y0 + y1) / 2) * SVG_W;
    const textWSvg = Math.max(midHalfW * 1.55, 160);
    const textW = (textWSvg / SVG_W) * funnelW;
    const textX = funnelX + (funnelW - textW) / 2;

    if (stage.showLess) {
      const lessYSvg = y0 * SVG_H + 11;
      const lessH = (22 / SVG_H) * funnelH;
      slide.addText("Less", {
        x: textX,
        y: funnelY + (lessYSvg / SVG_H) * funnelH - lessH / 2,
        w: textW,
        h: lessH,
        fontFace: FONTS.body,
        fontSize: lessPt,
        italic: true,
        color: COLORS.white,
        align: "center",
        valign: "middle",
        margin: 0,
      });
    }

    if (stage.stackVolumes) {
      const blockH = (bandHSvg * 0.72) / SVG_H * funnelH;
      const blockY = funnelY + (contentTopSvg / SVG_H) * funnelH;
      slide.addText(
        [
          {
            text: metric.count.toLocaleString("en-GB"),
            options: {
              fontFace: FONTS.body,
              fontSize: countPt,
              bold: true,
              color: COLORS.white,
              breakLine: true,
            },
          },
          {
            text: stage.label,
            options: {
              fontFace: FONTS.body,
              fontSize: labelPt,
              bold: true,
              color: COLORS.white,
              breakLine: true,
            },
          },
          {
            text: formatBn(metric.volume_gbp, "£"),
            options: {
              fontFace: FONTS.body,
              fontSize: volsPt,
              color: COLORS.white,
              breakLine: true,
            },
          },
          {
            text: formatBn(metric.volume_eur, "€"),
            options: {
              fontFace: FONTS.body,
              fontSize: volsPt,
              color: COLORS.white,
              breakLine: true,
            },
          },
          {
            text: formatBn(metric.volume_usd, "$"),
            options: {
              fontFace: FONTS.body,
              fontSize: volsPt,
              color: COLORS.white,
            },
          },
        ],
        {
          x: textX,
          y: blockY,
          w: textW,
          h: blockH,
          align: "center",
          valign: "top",
          margin: 0,
        },
      );
    } else {
      const blockH = (70 / SVG_H) * funnelH;
      const blockY = funnelY + (midYSvg / SVG_H) * funnelH - blockH / 2 - 0.02;
      slide.addText(
        [
          {
            text: metric.count.toLocaleString("en-GB"),
            options: {
              fontFace: FONTS.body,
              fontSize: countPt,
              bold: true,
              color: COLORS.white,
              breakLine: true,
            },
          },
          {
            text: stage.label,
            options: {
              fontFace: FONTS.body,
              fontSize: labelPt,
              bold: true,
              color: COLORS.white,
              breakLine: true,
            },
          },
          {
            text: formatVolumesInline(metric),
            options: {
              fontFace: FONTS.body,
              fontSize: volsPt,
              color: COLORS.white,
            },
          },
        ],
        {
          x: textX,
          y: blockY,
          w: textW,
          h: blockH,
          align: "center",
          valign: "middle",
          margin: 0,
        },
      );
    }
  }

  const breakdownX = funnelX + funnelW + gap;
  const breakdownRight = SLIDE_W - bodyPadX;
  const breakdownW = breakdownRight - breakdownX;

  const hubW = Math.min(2.35, breakdownW * 0.38);
  const hubH = 0.82;
  const hubX = breakdownX;
  const hubY = funnelY + (funnelH - hubH) / 2;

  const stemW = 0.375;
  const stemH = 0.02;
  const spineGap = 0.29;
  const spineX = hubX + hubW + stemW;
  const statusX = spineX + spineGap;
  const statusW = breakdownRight - statusX;
  const branchGap = 0.12;
  const statusH =
    (funnelH - branchGap * (BREAKDOWN_ITEMS.length - 1)) /
    BREAKDOWN_ITEMS.length;

  addShape(slide, ROUNDED_RECTANGLE, {
    x: hubX,
    y: hubY,
    w: hubW,
    h: hubH,
    fill: { color: COLORS.hub },
    line: { color: COLORS.hub, width: 0 },
    rectRadius: 0.18,
    shadow: {
      type: "outer",
      color: "000000",
      blur: 3,
      offset: 1,
      opacity: 0.12,
    },
  });
  slide.addText(`${active.count.toLocaleString("en-GB")} Active Deals`, {
    x: hubX,
    y: hubY,
    w: hubW,
    h: hubH,
    fontFace: FONTS.body,
    fontSize: 16,
    bold: true,
    color: COLORS.white,
    align: "center",
    valign: "middle",
    margin: 0,
  });

  addShape(slide, RECTANGLE, {
    x: hubX + hubW,
    y: hubY + hubH / 2 - stemH / 2,
    w: stemW,
    h: stemH,
    fill: { color: COLORS.line },
    line: { color: COLORS.line, width: 0 },
  });

  const spineTop = funnelY + funnelH * 0.08;
  const spineBottom = funnelY + funnelH * 0.92;
  addShape(slide, RECTANGLE, {
    x: spineX,
    y: spineTop,
    w: stemH,
    h: spineBottom - spineTop,
    fill: { color: COLORS.line },
    line: { color: COLORS.line, width: 0 },
  });

  BREAKDOWN_ITEMS.forEach((item, index) => {
    const metric = dealFlow.active_breakdown[item.key] ?? emptyMetric();
    const statusY = funnelY + index * (statusH + branchGap);
    const stubY = statusY + statusH / 2 - stemH / 2;

    addShape(slide, RECTANGLE, {
      x: spineX,
      y: stubY,
      w: spineGap,
      h: stemH,
      fill: { color: COLORS.line },
      line: { color: COLORS.line, width: 0 },
    });

    addShape(slide, ROUNDED_RECTANGLE, {
      x: statusX,
      y: statusY,
      w: statusW,
      h: statusH,
      fill: { color: item.bg },
      line: { color: item.bg, width: 0 },
      rectRadius: 0.16,
      shadow: {
        type: "outer",
        color: "000000",
        blur: 2,
        offset: 1,
        opacity: 0.1,
      },
    });

    slide.addText(
      `${metric.count.toLocaleString("en-GB")} ${item.label}`,
      {
        x: statusX,
        y: statusY,
        w: statusW,
        h: statusH,
        fontFace: FONTS.body,
        fontSize: 14,
        bold: true,
        color: COLORS.white,
        align: "center",
        valign: "middle",
        margin: 0,
      },
    );
  });

  return slide;
}
